import MonolithNotesAPI from "@/api/Notes/index.js";
import { toast } from "@/utils/toastService";
import type { HandleImageUrlUpload } from "@monolith-forensics/monolith-ui";

const IMPORT_FAILED_MESSAGE =
  "An image could not be imported. It may require you to sign in at its source.";

const notifyImportFailed = () => {
  // One aggregated toast per paste burst; emails often contain several
  // unfetchable images (e.g. tracking pixels) that would each fire a toast.
  toast.warning(IMPORT_FAILED_MESSAGE, {
    preventDuplicate: true,
    key: "note-image-url-import-failed",
  });
};

// Builds the RichTextEditor handleImageUrlUpload callback for a note surface.
// The editor invokes it when a pasted <img> URL can't be fetched by the
// browser (CORS); the server downloads the image into case storage instead.
export const createImageUrlUploadHandler = (
  getNoteContext: () => { case_uuid?: string; note_uuid?: string }
): HandleImageUrlUpload => {
  return async (imageSource) => {
    const { case_uuid, note_uuid } = getNoteContext();

    if (!case_uuid || !note_uuid) {
      notifyImportFailed();
      return undefined;
    }

    try {
      const res = await MonolithNotesAPI.downloadImageFromUrl({
        case_uuid,
        note_uuid,
        src: imageSource.src,
      });

      // Returning undefined makes the editor drop the image placeholder
      if (!res?.success || !res.url || !res.filename) {
        notifyImportFailed();
        return undefined;
      }

      // The editor sets the <img> alt from imageSource.name after this
      // resolves, and reload re-signing derives the S3 key from alt (pasted
      // images carry no data-uuid), so the name must be the server's stored
      // filename verbatim.
      imageSource.name = res.filename;

      return res.url;
    } catch {
      notifyImportFailed();
      return undefined;
    }
  };
};
